import { useDispatch } from "react-redux";
import { MutableRefObject, useCallback } from "react";
import { useCompanionStreamingService } from "@hooks/useCompanionStreamingService";
import { HeygenAvatarService } from "@service/avatar/heygen-avatar.service";
import { EventType } from "@models/audio-message";
import { setProcessing, setTalking } from "@store/avatarSlice";

const useStreaming = ({
  sessionId,
  avatar,
}: {
  sessionId: string;
  avatar: MutableRefObject<HeygenAvatarService | null>;
}): {
  sendMessage: (
    audioBlob?: ArrayBufferLike,
    photoId?: string,
    eventType?: EventType,
  ) => Promise<void>;
  interruptPlayback: () => Promise<void>;
  suppressGalleryAssistantStream: () => void;
} => {
  const dispatch = useDispatch();

  const speak = useCallback(
    async (text: string) => {
      if (!avatar.current) {
        return;
      }
      await avatar.current.speak(text);
    },
    [avatar],
  );

  const { send, suppressGalleryAssistantStream } =
    useCompanionStreamingService(speak, false);

  const interruptPlayback = useCallback(async () => {
    dispatch(setTalking(false));
    dispatch(setProcessing(false));
    await avatar.current?.stopSpeaking();
  }, [avatar, dispatch]);

  const sendMessage = useCallback(
    async (
      audioBlob?: ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
    ): Promise<void> => {
      try {
        await send({
          audio: audioBlob || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
        });
      } catch (e) {
        console.error(e);
      }
    },
    [send, sessionId],
  );

  return {
    sendMessage,
    interruptPlayback,
    suppressGalleryAssistantStream,
  };
};

export default useStreaming;
